import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Route, RefreshCw, AlertOctagon, CalendarClock, Ban, CalendarArrowDown, CalendarArrowUp,
  Clock3, Send, Eye, ExternalLink, CheckCircle2, AlertTriangle, Loader2, XCircle, LogIn,
} from "lucide-react";
import BookingDetailDialog from "../components/BookingDetailDialog";

const BADGE = {
  pronto: { cls: "bg-emerald-100 text-emerald-700", label: "Pronto" },
  da_configurare: { cls: "bg-amber-100 text-amber-800", label: "Da configurare" },
  inviato: { cls: "bg-slate-200 text-slate-600", label: "Inviato" },
};
const fmtDT = (s) => s ? new Date(s).toLocaleString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—";
const fmtD = (s) => s ? new Date(s).toLocaleDateString("it-IT", { day: "2-digit", month: "short" }) : "—";

const SECTIONS = [
  { key: "urgenti", title: "Urgenti", icon: AlertOctagon, color: "#DC2626", type: "step" },
  { key: "oggi", title: "Da fare oggi", icon: CalendarClock, color: "#2563EB", type: "step" },
  { key: "bloccati", title: "Bloccati / da configurare", icon: Ban, color: "#D97706", type: "step" },
  { key: "arrivi", title: "Arrivi di oggi", icon: CalendarArrowDown, color: "#0891B2", type: "booking" },
  { key: "partenze", title: "Partenze di oggi", icon: CalendarArrowUp, color: "#7C3AED", type: "booking" },
  { key: "prossimi", title: "Prossimi 3 giorni", icon: Clock3, color: "#64748B", type: "step" },
];

function StepRow({ it, onOpen, onSend, sending }) {
  const [showPreview, setShowPreview] = useState(false);
  const b = BADGE[it.badge] || BADGE.da_configurare;
  const canSend = it.can_auto_send && it.status !== "sent";
  return (
    <div className="rounded-xl border border-slate-200 p-3" data-testid={`brief-item-${it.booking_id}-${it.cm_code}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          <span className="shrink-0 rounded-md bg-slate-800 px-1.5 py-0.5 font-mono text-[11px] font-semibold text-white">{it.cm_code}</span>
          <div className="min-w-0">
            <p className="truncate text-sm font-medium text-slate-800">{it.apartment} · {it.guest_name || "—"}</p>
            <p className="text-xs text-slate-400">#{it.booking_reference} · {it.cm_title}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="hidden items-center gap-1 text-xs text-slate-400 sm:flex"><Clock3 size={12} /> {fmtDT(it.scheduled_at)}</span>
          <Badge className={b.cls}>{b.label}</Badge>
        </div>
      </div>
      {!it.can_auto_send && (it.unresolved || []).length > 0 && (
        <p className="mt-2 rounded-lg bg-amber-50 px-2.5 py-1.5 text-xs text-amber-700" data-testid={`brief-block-${it.booking_id}-${it.cm_code}`}>
          <AlertTriangle size={12} className="mr-1 inline" /> Bloccato: {(it.unresolved).map((u) => u.placeholder).join(", ")} non risolti
        </p>
      )}
      {showPreview && (
        <div className="mt-2 whitespace-pre-wrap rounded-lg bg-slate-50 p-2.5 text-xs text-slate-700" data-testid={`brief-preview-${it.booking_id}-${it.cm_code}`}>{it.preview}</div>
      )}
      <div className="mt-2 flex flex-wrap gap-1.5">
        <Button size="sm" variant="outline" className="h-8 gap-1 text-xs" data-testid={`brief-open-${it.booking_id}-${it.cm_code}`} onClick={() => onOpen(it.booking_id)}><ExternalLink size={12} /> Apri prenotazione</Button>
        <Button size="sm" variant="ghost" className="h-8 gap-1 text-xs" onClick={() => setShowPreview((v) => !v)}><Eye size={12} /> Anteprima</Button>
        {canSend && (
          <Button size="sm" className="h-8 gap-1 text-xs" data-testid={`brief-send-${it.booking_id}-${it.cm_code}`} disabled={sending} onClick={() => onSend(it)}>
            {sending ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />} Invia
          </Button>
        )}
      </div>
    </div>
  );
}

function BookingRow({ it, onOpen }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-slate-200 p-3" data-testid={`brief-booking-${it.booking_id}`}>
      <div className="min-w-0">
        <p className="truncate text-sm font-medium text-slate-800">{it.apartment} · {it.guest_name || "—"}</p>
        <p className="text-xs text-slate-400">#{it.booking_reference} · {it.kind === "arrivo" ? `check-in ${fmtD(it.check_in)}` : `check-out ${fmtD(it.check_out)}`}
          {it.kind === "arrivo" && !it.checkin_completed && <span className="ml-1 text-amber-600">· check-in incompleto</span>}
          {it.kind === "arrivo" && it.document_status !== "ricevuto" && <span className="ml-1 text-rose-600">· documenti mancanti</span>}
        </p>
      </div>
      <Button size="sm" variant="outline" className="h-8 gap-1 text-xs" data-testid={`brief-open-booking-${it.booking_id}`} onClick={() => onOpen(it.booking_id)}><ExternalLink size={12} /> Apri</Button>
    </div>
  );
}

export default function DailyBrief() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState("");
  const [detailId, setDetailId] = useState(null);
  const baseUrl = typeof window !== "undefined" ? window.location.origin : "";

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/comm/daily-brief?base_url=${encodeURIComponent(baseUrl)}&days=3`)
      .then((r) => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
  }, [baseUrl]);
  useEffect(() => { load(); }, [load]);

  const onSend = async (it) => {
    setSending(`${it.booking_id}-${it.cm_code}`);
    try {
      await api.post(`/comm/journey/${it.booking_id}/${it.cm_code}/send`, { channel: it.channel, base_url: baseUrl });
      toast.success(`${it.cm_code} inviato`);
      load();
    } catch (e) {
      const det = e?.response?.data?.detail;
      toast.error(det && det.unresolved ? `Bloccato: ${det.unresolved.map((u) => u.placeholder).join(", ")}` : (det || "Errore invio"));
    } finally { setSending(""); }
  };

  if (loading) return <div className="flex items-center gap-2 py-10 text-slate-400" data-testid="brief-loading"><Loader2 className="animate-spin" size={18} /> Caricamento riepilogo…</div>;
  if (!data) return <p className="py-10 text-slate-400">Riepilogo non disponibile.</p>;

  const s = data.summary;
  const KPIS = [
    { k: "da_inviare_oggi", label: "Da inviare oggi", icon: Send, color: "#2563EB" },
    { k: "pronti", label: "Pronti", icon: CheckCircle2, color: "#059669" },
    { k: "bloccati", label: "Bloccati", icon: AlertTriangle, color: "#D97706" },
    { k: "invii_falliti", label: "Invii falliti", icon: XCircle, color: "#DC2626" },
    { k: "checkin_incompleti", label: "Check-in incompleti", icon: LogIn, color: "#7C3AED" },
    { k: "documenti_mancanti", label: "Documenti mancanti", icon: AlertOctagon, color: "#DB2777" },
    { k: "accessi_da_inviare", label: "Accessi da inviare", icon: Route, color: "#0891B2" },
    { k: "checkout_previsti", label: "Check-out previsti", icon: CalendarArrowUp, color: "#EA580C" },
    { k: "recensioni", label: "Recensioni", icon: CheckCircle2, color: "#16A34A" },
    { k: "senza_step", label: "Senza prossimo step", icon: Ban, color: "#64748B" },
  ];

  return (
    <div className="space-y-6 fade-in" data-testid="daily-brief-page">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Route size={20} className="text-indigo-600" />
          <div>
            <h2 className="font-head text-xl font-semibold text-slate-900">Riepilogo Operativo Giornaliero</h2>
            <p className="text-xs text-slate-400">Ivy · aggiornato {fmtDT(data.generated_at)}</p>
          </div>
        </div>
        <Button variant="outline" size="sm" className="gap-1.5" data-testid="brief-refresh" onClick={load}><RefreshCw size={14} /> Aggiorna</Button>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        {KPIS.map((c) => {
          const Icon = c.icon;
          return (
            <Card key={c.k} data-testid={`brief-kpi-${c.k}`} className="p-3.5">
              <div className="flex items-center gap-2 text-xs text-slate-500"><Icon size={14} style={{ color: c.color }} /> {c.label}</div>
              <div className="mt-1 text-2xl font-bold text-slate-900">{s[c.k] ?? 0}</div>
            </Card>
          );
        })}
      </div>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        {SECTIONS.map((sec) => {
          const items = data.sections[sec.key] || [];
          const Icon = sec.icon;
          return (
            <Card key={sec.key} className="p-5" data-testid={`brief-section-${sec.key}`}>
              <div className="mb-3 flex items-center gap-2">
                <Icon size={16} style={{ color: sec.color }} />
                <h3 className="font-head text-base font-semibold text-slate-800">{sec.title}</h3>
                <Badge className="ml-auto bg-slate-100 text-slate-600">{items.length}</Badge>
              </div>
              {items.length === 0 ? (
                <p className="py-4 text-center text-sm text-slate-400">Nessuna attività</p>
              ) : (
                <div className="space-y-2">
                  {items.map((it) => sec.type === "booking"
                    ? <BookingRow key={it.booking_id} it={it} onOpen={setDetailId} />
                    : <StepRow key={`${it.booking_id}-${it.cm_code}`} it={it} onOpen={setDetailId} onSend={onSend} sending={sending === `${it.booking_id}-${it.cm_code}`} />)}
                </div>
              )}
            </Card>
          );
        })}
      </div>

      {detailId && (
        <BookingDetailDialog open={!!detailId} onOpenChange={(o) => { if (!o) { setDetailId(null); load(); } }} bookingId={detailId} />
      )}
    </div>
  );
}
